import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAction, useMutation, useQuery } from "convex/react";
import { api, type Id } from "../lib/api";
import { useCompany } from "../lib/company";
import {
  activeBillingPlan,
  billingNoticeToneClass,
  billingNoticeView,
  billingPortalButtonView,
  billingPlanCardView,
  billingReturnParamsErrorMessage,
  canSelfServeSubscribe,
  currentPlanDisplayState,
  emptyBillingWorkflowState,
  formatPlanPrice,
  nextBillingWorkflowState,
  parseBillingReturnParams,
  premiumCheckoutButtonView,
  premiumCheckoutCommand,
  premiumCheckoutPlan,
  sortPlansByPrice,
  visibleBillingPlans,
  type BillingNoticeView,
  type BillingPlanCardView,
  type CurrentPlanDisplayState,
} from "../domain/billing";
import { availableCoverageLegislation, canUsePremiumAlerts, coverageBundleActionView, coverageBundleView, coverageCatalogueView, premiumCoverageToggleView, subscriptionLegislationIds } from "../domain/subscriptions";
import { JURISDICTION_LABELS } from "../../convex/model/enums";
import { caughtErrorMessage, prefixedCaughtErrorMessage } from "../domain/errors";
import { Button, Card, Icon, JurisBadge, TierBadge, cx } from "../components/ui";
import { searchParamsWithoutKeys } from "../domain/routeParams";
import { MonitoringNav } from "../components/MonitoringNav";

const BILLING_RETURN_SEARCH_KEYS = ["billing", "session_id"] as const;

function ChoosePlanPrompt() {
  return (
    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
      An active plan is required to change monitoring.{" "}
      <a href="/account/billing" className="font-semibold underline hover:text-amber-950">
        View plan and billing options
      </a>.
    </p>
  );
}

function CurrentPlanLabel({ state }: { state: CurrentPlanDisplayState }) {
  switch (state.kind) {
    case "plan":
      return (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded bg-[var(--accent-soft)] px-2 py-0.5 text-[12px] font-semibold text-[var(--accent-dark)] ring-1 ring-inset ring-[color:var(--accent)]/20">
            {state.name}
          </span>
          <TierBadge tier={state.tier} />
        </span>
      );
    case "trial":
      return (
        <span className="inline-flex items-center gap-1.5 rounded bg-sky-50 px-2 py-0.5 text-[12px] font-semibold text-sky-700 ring-1 ring-inset ring-sky-200">
          <Icon name="clock" size={12} />
          {state.daysRemaining === null ? "Trial active" : <>Trial · {state.daysRemaining} {state.daysRemaining === 1 ? "day" : "days"} remaining</>}
        </span>
      );
    case "none":
      return (
        <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-[12px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
          No active plan
        </span>
      );
  }
}

function BillingNotice({ view }: { view: BillingNoticeView }) {
  if (view.kind === "none") return null;
  return (
    <div className={cx("rounded-lg border px-4 py-3 text-sm", billingNoticeToneClass(view))}>
      {view.message}
    </div>
  );
}

function PlanCardAction({ view }: { view: BillingPlanCardView }) {
  switch (view.actionTone) {
    case "current":
      return (
        <span className="rounded bg-[var(--accent-soft)] px-2 py-0.5 text-[11px] font-semibold text-[var(--accent-dark)] ring-1 ring-inset ring-[color:var(--accent)]/20">
          {view.actionLabel}
        </span>
      );
    case "muted":
      return <span className="text-[12px] font-medium text-slate-500">{view.actionLabel}</span>;
    case "primary":
      return <span className="text-[12px] font-medium text-[var(--accent-dark)]">{view.actionLabel}</span>;
  }
}

export function Subscriptions({ mode = "monitoring" }: { readonly mode?: "monitoring" | "billing" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { company, companyId } = useCompany();
  const runtime = useQuery(api.runtime.settings, {});
  const subs = useQuery(api.subscriptions.listForCompany, companyId !== null ? { companyId } : "skip");
  const bundles = useQuery(api.bundles.list, {}) ?? [];
  const rawLegislation = useQuery(api.legislation.list, { status: "active" });
  const allLegislation = useMemo(() => rawLegislation ?? [], [rawLegislation]);
  const rawPlans = useQuery(api.plans.list);
  const plans = useMemo(() => rawPlans ?? [], [rawPlans]);

  const subscribe = useMutation(api.subscriptions.subscribe);
  const unsubscribe = useMutation(api.subscriptions.unsubscribe);
  const applyBundle = useMutation(api.subscriptions.applyBundle);
  const createCheckout = useAction(api.billing.createCheckoutSession);
  const reconcileCheckout = useAction(api.billing.reconcileCheckoutReturn);
  const createPortal = useAction(api.billing.createPortalSession);

  const billingReturnParse = useMemo(() => parseBillingReturnParams({
    billing: searchParams.get("billing"),
    sessionId: searchParams.get("session_id"),
  }), [searchParams]);

  const [search, setSearch] = useState("");
  const [asPremium, setAsPremium] = useState(false);
  const [billingWorkflow, dispatchBillingWorkflow] = useReducer(
    nextBillingWorkflowState<Id<"plans">>,
    undefined,
    () => emptyBillingWorkflowState<Id<"plans">>(),
  );
  const [coverageBusyKey, setCoverageBusyKey] = useState<string | null>(null);
  const [coverageNotice, setCoverageNotice] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [pendingRemovalId, setPendingRemovalId] = useState<Id<"legislation"> | null>(null);
  const {
    checkoutPlanId,
    premiumCheckoutBusy,
    portalBusy,
    billingError,
    checkoutNotice,
    billingSyncing,
    billingReturn,
  } = billingWorkflow;
  const billingReturnStarted = useRef(false);
  const billingRefreshStarted = useRef(false);

  const subscribedIds = useMemo(() => subscriptionLegislationIds(subs ?? []), [subs]);
  const sortedPlans = useMemo(() => sortPlansByPrice(plans), [plans]);
  const premiumPlan = useMemo(() => premiumCheckoutPlan(sortedPlans), [sortedPlans]);
  const available = useMemo(
    () => availableCoverageLegislation({ legislation: allLegislation, subscribedIds, query: search }),
    [allLegislation, search, subscribedIds],
  );
  const coverageCatalogue = useMemo(
    () => coverageCatalogueView({
      availableCount: available.length,
      subscribedCount: subscribedIds.size,
      query: search,
    }),
    [available.length, search, subscribedIds.size],
  );

  useEffect(() => {
    if (billingReturnParse.ok) {
      if (billingReturnParse.value !== null) {
        dispatchBillingWorkflow({ type: "billingReturnDetected", value: billingReturnParse.value });
      }
      return;
    }

    dispatchBillingWorkflow({
      type: "billingReturnParseFailed",
      error: billingReturnParamsErrorMessage(billingReturnParse.error),
    });
    setSearchParams(searchParamsWithoutKeys(searchParams, BILLING_RETURN_SEARCH_KEYS), { replace: true });
  }, [billingReturnParse, searchParams, setSearchParams]);

  useEffect(() => {
    if (location.hash !== "#coverage") return;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById("coverage");
      const scroller = target?.closest<HTMLElement>("[data-page-scroll]");
      if (target !== null && scroller !== null && scroller !== undefined) {
        scroller.scrollTo({ top: Math.max(target.offsetTop - 16, 0), behavior: "smooth" });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  useEffect(() => {
    if (billingReturn === null) return;
    dispatchBillingWorkflow({ type: "billingReturnNoticeShown", status: billingReturn.status });

    setSearchParams(searchParamsWithoutKeys(searchParams, BILLING_RETURN_SEARCH_KEYS), { replace: true });

    if (billingReturn.status === "cancelled") {
      billingReturnStarted.current = true;
      dispatchBillingWorkflow({ type: "billingReturnCleared" });
      return;
    }
    if (companyId === null || billingReturnStarted.current) return;

    billingReturnStarted.current = true;
    dispatchBillingWorkflow({ type: "billingSyncStarted" });
    void reconcileCheckout({
      companyId,
      ...(billingReturn.sessionId !== null ? { checkoutSessionId: billingReturn.sessionId } : {}),
    })
      .then(() => {
        dispatchBillingWorkflow({ type: "billingSyncSucceeded", clearReturn: true });
      })
      .catch((e) => {
        dispatchBillingWorkflow({
          type: "billingSyncFailed",
          error: prefixedCaughtErrorMessage(
            e,
            "Payment received, but plan sync failed: ",
            "Payment received, but plan sync failed.",
          ),
          clearReturn: true,
        });
      });
  }, [billingReturn, companyId, reconcileCheckout, searchParams, setSearchParams]);

  useEffect(() => {
    if (
      billingReturn !== null ||
      checkoutNotice !== null ||
      companyId === null ||
      company === null ||
      company.stripeCustomerId === null ||
      company.activePlanId !== null ||
      billingRefreshStarted.current
    ) return;

    billingRefreshStarted.current = true;
    dispatchBillingWorkflow({ type: "billingSyncStarted" });
    void reconcileCheckout({ companyId })
      .then(() => {
        dispatchBillingWorkflow({ type: "billingSyncSucceeded", clearReturn: false });
      })
      .catch((e) => {
        dispatchBillingWorkflow({
          type: "billingSyncFailed",
          error: prefixedCaughtErrorMessage(
            e,
            "Could not refresh Stripe billing status: ",
            "Could not refresh Stripe billing status.",
          ),
          clearReturn: false,
        });
      });
  }, [billingReturn, checkoutNotice, company, companyId, reconcileCheckout]);

  if (companyId === null) {
    return <p className="px-9 py-7 text-slate-500">No company selected.</p>;
  }

  if (company === null) {
    return <p className="px-9 py-7 text-slate-500">Loading…</p>;
  }

  const now = Date.now();
  const selfServe = runtime?.staging === true || canSelfServeSubscribe(company, now);
  const plan = activeBillingPlan(plans, company.activePlanId);
  const premiumEntitled = canUsePremiumAlerts(plan);
  const checkoutBusy = checkoutPlanId !== null || premiumCheckoutBusy;
  const premiumToggle = premiumCoverageToggleView({ selfServe, premiumEntitled, asPremium });
  // Only purchasable plans are offered; the current plan stays visible even if
  // it has no Stripe price (e.g. the demo plan), so "Current plan" still renders.
  const visiblePlans = visibleBillingPlans(sortedPlans, company.activePlanId);

  const startCheckout = async (planId: Id<"plans">) => {
    dispatchBillingWorkflow({ type: "checkoutStarted", planId });
    try {
      const { url } = await createCheckout({
        companyId,
        planId,
        origin: window.location.origin,
      });
      window.location.href = url;
    } catch (e) {
      dispatchBillingWorkflow({ type: "checkoutFailed", error: caughtErrorMessage(e, "Checkout failed") });
    }
  };

  const startPremiumCheckout = async () => {
    const command = premiumCheckoutCommand(premiumPlan);
    if (command.kind === "unavailable") {
      dispatchBillingWorkflow({ type: "premiumCheckoutUnavailable", message: command.message });
      return;
    }
    dispatchBillingWorkflow({ type: "premiumCheckoutStarted" });
    try {
      const { url } = await createCheckout({
        companyId,
        planId: command.planId,
        origin: window.location.origin,
      });
      window.location.href = url;
    } catch (e) {
      dispatchBillingWorkflow({ type: "premiumCheckoutFailed", error: caughtErrorMessage(e, "Checkout failed") });
    }
  };

  const openPortal = async () => {
    dispatchBillingWorkflow({ type: "portalStarted" });
    try {
      const { url } = await createPortal({ companyId, origin: window.location.origin });
      window.location.href = url;
    } catch (e) {
      dispatchBillingWorkflow({ type: "portalFailed", error: caughtErrorMessage(e, "Could not open billing portal") });
    }
  };

  const runCoverageChange = async (
    key: string,
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    setCoverageBusyKey(key);
    setCoverageNotice(null);
    try {
      await action();
      setCoverageNotice({ tone: "success", text: successMessage });
      setPendingRemovalId(null);
    } catch (error) {
      setCoverageNotice({
        tone: "error",
        text: caughtErrorMessage(error, "Could not update coverage."),
      });
    } finally {
      setCoverageBusyKey(null);
    }
  };

  const notice = billingNoticeView(checkoutNotice, billingSyncing);
  const portalButton = billingPortalButtonView(portalBusy);
  const premiumCheckoutButton = premiumCheckoutButtonView({ checkoutBusy, premiumCheckoutBusy });

  const currentPlanState = currentPlanDisplayState({ plan, company, now });

  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-8 px-6 py-7 lg:px-9">
      <div>
        <div className="text-[13px] font-medium text-[var(--accent-dark)]">{company.name}</div>
        <h2 className="mt-1 font-serif text-[29px] font-semibold tracking-tight text-slate-950">{mode === "billing" ? "Plan & billing" : "Monitoring"}</h2>
        <p className="mt-1 max-w-[760px] text-[14px] leading-relaxed text-slate-500">
          {mode === "billing"
            ? "See what your plan enables and manage the account used for ongoing monitoring."
            : "Configure the legislation Lawstream watches. Detected changes and affected documents appear in your Updates inbox."}
        </p>
      </div>

      {mode === "monitoring" && <>
      <MonitoringNav active="setup" />

      <div className="rounded-xl border border-sky-200 bg-sky-50 px-5 py-4 text-sky-900">
        <div className="flex flex-wrap items-center gap-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-sky-700 ring-1 ring-sky-200">
            <Icon name="bell" size={19} />
          </span>
          <div className="min-w-[240px] flex-1">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-sky-700">Monitoring status</div>
            <h2 className="mt-0.5 text-[15px] font-semibold text-slate-900">{subs?.length ?? 0} {(subs?.length ?? 0) === 1 ? "law" : "laws"} monitored</h2>
            <p className="mt-1 max-w-[760px] text-[13.5px] leading-relaxed text-sky-800/80">
              {runtime?.staging
                ? "Staging workspace: monitoring and Updates can be tested here. Outbound emails and scheduled source imports are disabled."
                : "When these sources change, Lawstream adds the change to Updates and emails the team members assigned to affected documents."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" icon="users" onClick={() => navigate("/documents")}>Assign staff to documents</Button>
            <Button variant="outline" size="sm" icon="bell" onClick={() => navigate("/updates")}>Open updates</Button>
          </div>
        </div>
      </div>

      <BillingNotice view={notice} />

      {coverageNotice !== null && (
        <div
          aria-live="polite"
          className={cx(
            "rounded-lg border px-4 py-3 text-sm",
            coverageNotice.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-rose-200 bg-rose-50 text-rose-800",
          )}
        >
          {coverageNotice.text}
        </div>
      )}

      {/* current subscriptions */}
      <section id="coverage">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-serif text-[20px] font-semibold text-slate-900">
            Monitored legislation <span className="font-sans text-[15px] font-normal text-slate-300">· {subs?.length ?? 0}</span>
          </h2>
        </div>
        {subs === undefined ? (
          <p className="text-slate-500">Loading…</p>
        ) : subs.length === 0 ? (
          <Card className="px-5 py-10 text-center text-sm text-slate-400">No legislation is monitored yet. Add a bundle or individual law below.</Card>
        ) : (
          <Card className="divide-y divide-slate-100 overflow-hidden">
            {subs.map((s) => (
              <div key={s._id} className="flex items-center gap-4 px-5 py-3.5 transition hover:bg-slate-50/60">
                {s.legislation && <JurisBadge code={s.legislation.jurisdiction} />}
                <div className="min-w-0 flex-1">
                  <button onClick={() => navigate(`/legislation/${s.legislationId}`)} className="block truncate text-left text-[14px] font-medium text-slate-800 hover:text-[var(--accent-dark)]">
                    {s.legislation?.title ?? "(unknown)"}
                  </button>
                  <div className="mt-0.5 text-[12px] text-slate-400">via {s.source}</div>
                </div>
                <TierBadge tier={s.subscriptionType} />
                {pendingRemovalId === s.legislationId ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] text-rose-700">Remove?</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={coverageBusyKey !== null}
                      onClick={() => void runCoverageChange(
                        `remove:${s.legislationId}`,
                        () => unsubscribe({ companyId, legislationId: s.legislationId }),
                        `Removed ${s.legislation?.title ?? "legislation"} from coverage.`,
                      )}
                    >
                      {coverageBusyKey === `remove:${s.legislationId}` ? "Removing..." : "Confirm"}
                    </Button>
                    <button
                      type="button"
                      disabled={coverageBusyKey !== null}
                      onClick={() => setPendingRemovalId(null)}
                      className="px-1 text-[12px] text-slate-500 hover:text-slate-800"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={coverageBusyKey !== null}
                    onClick={() => setPendingRemovalId(s.legislationId)}
                    className="px-2 text-[12.5px] font-medium text-slate-400 hover:text-red-600 disabled:opacity-40"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </Card>
        )}
      </section>

      {/* bundles */}
      <section>
        <div className="mb-3">
          <h2 className="font-serif text-[20px] font-semibold text-slate-900">Curated bundles</h2>
          <p className="mt-0.5 text-[13px] text-slate-500">Add a maintained set of related legislation to monitoring in one step.</p>
        </div>
        {!selfServe && <div className="mb-3"><ChoosePlanPrompt /></div>}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {bundles.map((b) => {
            const bundleView = coverageBundleView({ legislationIds: b.legislationIds, subscribedIds, selfServe });
            const bundleAction = coverageBundleActionView(bundleView);
            return (
              <Card key={b._id} className="flex flex-col p-5">
                <div className="mb-1 flex items-center gap-2.5">
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--accent-soft)] text-[var(--accent-dark)]"><Icon name="shield" size={18} /></span>
                  <div>
                    <h3 className="font-serif text-[18px] font-semibold leading-tight text-slate-900">{b.name}</h3>
                    <div className="text-[12px] text-slate-400">{JURISDICTION_LABELS[b.jurisdiction]} · {bundleView.instrumentCountLabel}</div>
                  </div>
                </div>
                {b.description !== null && <p className="mb-3 mt-2 text-[13px] leading-relaxed text-slate-500">{b.description}</p>}
                <div className="mt-auto pt-2">
                  <Button
                    variant={bundleAction.variant}
                    size="sm"
                    className="w-full"
                    icon={bundleAction.icon}
                    disabled={bundleAction.disabled || coverageBusyKey !== null}
                    onClick={bundleAction.action === "applyBundle"
                      ? () => void runCoverageChange(
                          `bundle:${b._id}`,
                          () => applyBundle({ companyId, bundleId: b._id }),
                          `Added the ${b.name} bundle to coverage.`,
                        )
                      : undefined}
                  >
                    {coverageBusyKey === `bundle:${b._id}` ? "Adding bundle..." : bundleAction.label}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* à la carte */}
      <section>
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-serif text-[20px] font-semibold text-slate-900">Add individual laws</h2>
          <label className={cx("flex items-center gap-2 text-[12.5px]", premiumToggle.labelToneClass)} title={premiumToggle.title}>
            <input type="checkbox" disabled={premiumToggle.disabled} checked={premiumToggle.checked} onChange={(e) => setAsPremium(e.target.checked)} className="h-3.5 w-3.5 accent-[var(--accent)]" />
            Subscribe as premium
          </label>
        </div>
        <p className="mb-3 text-[13px] text-slate-500">Browse the catalogue and choose additional legislation to monitor.</p>
        {!selfServe && <div className="mb-3"><ChoosePlanPrompt /></div>}
        <div className="mb-3 flex max-w-[640px] items-center gap-2.5 rounded-xl bg-white px-3.5 py-2.5 shadow-sm ring-1 ring-slate-200">
          <Icon name="search" size={18} className="text-slate-400" />
          <input aria-label="Filter legislation catalogue" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Filter ${allLegislation.length} instruments…`} className="w-full bg-transparent text-[14px] outline-none placeholder:text-slate-400" />
        </div>
        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3 text-[12.5px] text-slate-500">
            <span>{coverageCatalogue.availableCountLabel}</span>
            <span>{coverageCatalogue.subscribedCountLabel}</span>
          </div>
          <div className="max-h-[34rem] divide-y divide-slate-100 overflow-y-auto">
            {available.map((l) => (
              <div key={l._id} className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <JurisBadge code={l.jurisdiction} />
                  <span className="truncate">{l.title}</span>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  icon="plus"
                  disabled={!selfServe || coverageBusyKey !== null}
                  onClick={() => void runCoverageChange(
                    `add:${l._id}`,
                    () => subscribe({
                      companyId,
                      legislationId: l._id,
                      requestedType: premiumToggle.requestedType,
                    }),
                    `Added ${l.title} to coverage.`,
                  )}
                >
                  {coverageBusyKey === `add:${l._id}` ? "Adding..." : "Add"}
                </Button>
              </div>
            ))}
            {available.length === 0 && (
              <div className="px-5 py-6 text-center text-sm text-slate-400">
                {coverageCatalogue.emptyMessage}
              </div>
            )}
          </div>
        </Card>
      </section>
      </>}
      {mode === "billing" && <>
      {/* plan and billing */}
      <section id="billing-plans">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-serif text-[20px] font-semibold text-slate-900">Plan capabilities</h2>
            <p className="mt-1 text-[13px] text-slate-500">
              Choose the monitoring capability this workspace needs, or manage the current subscription.
            </p>
            <div className="mt-1.5"><CurrentPlanLabel state={currentPlanState} /></div>
          </div>
          {company.stripeCustomerId !== null && (
            <Button variant="outline" size="sm" icon="ext" disabled={portalButton.disabled} onClick={() => void openPortal()}>
              {portalButton.label}
            </Button>
          )}
        </div>
        {billingError !== null && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{billingError}</div>
        )}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visiblePlans.map((p) => {
            const cardView = billingPlanCardView({
              planId: p._id,
              activePlanId: company.activePlanId,
              checkoutPlanId,
              checkoutBusy,
            });
            return (
              <button
                key={p._id}
                type="button"
                disabled={cardView.disabled}
                onClick={() => void startCheckout(p._id)}
                className={cx(
                  "text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2",
                  cardView.cursor === "default" ? "cursor-default" : "cursor-pointer",
                )}
              >
                <Card
                  className={cx(
                    "flex h-full flex-col p-5",
                    cardView.highlighted && "ring-2 ring-[color:var(--accent)]",
                    cardView.interactive && "hover:shadow-md hover:ring-slate-300",
                    cardView.busy && "opacity-70",
                  )}
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <h3 className="font-serif text-[18px] font-semibold leading-tight text-slate-900">{p.name}</h3>
                    <TierBadge tier={p.tier} />
                  </div>
                  {p.description !== null && (
                    <p className="mb-3 text-[13px] leading-relaxed text-slate-500">{p.description}</p>
                  )}
                  <div className="mt-auto flex items-end justify-between gap-2 pt-2">
                    <div className="font-serif text-[20px] font-semibold text-slate-900">
                      {formatPlanPrice(p.priceAudCents, p.interval)}
                    </div>
                    <PlanCardAction view={cardView} />
                  </div>
                </Card>
              </button>
            );
          })}
        </div>
      </section>

      {premiumEntitled ? (
        <div className="overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-emerald-900">
          <div className="flex flex-wrap items-center gap-4">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-emerald-700 ring-1 ring-emerald-200">
              <Icon name="spark" size={19} fill />
            </span>
            <div className="min-w-[240px] flex-1">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-emerald-700">Premium alerts active</div>
              <h2 className="mt-0.5 text-[15px] font-semibold text-slate-900">{plan?.name ?? "Enterprise"} includes premium alert coverage.</h2>
              <p className="mt-1 text-[13px] leading-relaxed text-emerald-800/80">
                This plan enables premium alert coverage for monitored laws and affected mapped documents.
              </p>
            </div>
            {company.stripeCustomerId !== null && (
              <Button variant="outline" size="sm" icon="ext" disabled={portalButton.disabled} onClick={() => void openPortal()}>
                {portalButton.label}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-2xl bg-slate-900 text-white ring-1 ring-slate-800">
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
          <div className="relative flex flex-wrap items-center gap-7 px-7 py-6">
            <div className="min-w-[260px] flex-1">
              <div className="mb-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-300"><Icon name="spark" size={13} fill /> ENTERPRISE PLAN</div>
              <h2 className="font-serif text-[24px] font-semibold leading-tight">Enable ongoing monitoring when you are ready.</h2>
              <p className="mt-2 max-w-[560px] text-[13.5px] leading-relaxed text-slate-300">
                Enterprise enables real-time change notifications, amendment digests, and point-in-time history for your monitored legislation.
              </p>
              <div className="mt-4 flex items-center gap-2">
                <Button
                  size="md"
                  className="!bg-white !text-slate-900 hover:!bg-slate-100"
                  disabled={premiumCheckoutButton.disabled}
                  onClick={() => void startPremiumCheckout()}
                >
                  {premiumCheckoutButton.label}
                </Button>
              </div>
            </div>
            <div className="flex shrink-0 gap-5 text-center">
              {[["Real-time", "vs. weekly"], ["Point-in-time", "history"], ["Digest", "summaries"]].map((m) => (
                <div key={m[0]} className="px-3">
                  <div className="font-serif text-[20px] font-semibold text-amber-300">{m[0]}</div>
                  <div className="mt-0.5 text-[11.5px] text-slate-400">{m[1]}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>}

    </div>
  );
}
